import { Component, OnInit } from '@angular/core';
import { AccountBalanceDTO } from 'src/app/models/account/account-balance-dto';
import { Branch } from 'src/app/models/branch/branch';
import { Currency } from 'src/app/models/enums/currency';
import { Role } from 'src/app/core/models/role.enum';
import { CurrencySubTypes } from 'src/app/models/enums/currency-sub-types';
import { ApplicationUserService } from 'src/app/core/services/application-user.service';
import { WidgetService } from 'src/app/services/widget.service';
import { AccountTransactionService } from 'src/app/services/account-transaction.service';
import { BalanceKpiCard, balanceKpiCard, hasBalance } from '../balance-kpi-card';

@Component({
  selector: 'app-all-branches-balance',
  templateUrl: './all-branches-balance.component.html',
  styleUrls: ['./all-branches-balance.component.scss']
})
export class AllBranchesBalanceComponent implements OnInit {
  Currency = Currency;
  hasUserRole = false;
  loading = true;
  authorizedBranches: Branch[] = [];
  balances: AccountBalanceDTO[] = [];

  /**
   * Kartlar bakiyeler gelince bir kez üretilir: şablon her değişiklik algılamada aynı diziyi
   * görür (getter her turda yeni dizi döndürseydi kartlar her turda yeniden çizilirdi).
   */
  cards: BalanceKpiCard[] = [];

  /** Yüklenirken gösterilen iskelet kart sayısı. */
  readonly loadingPlaceholders = [1, 2, 3, 4];

  constructor(
    private applicationUserService: ApplicationUserService,
    private widgetService: WidgetService,
    private accountTransactionService: AccountTransactionService
  ) {}

  ngOnInit(): void {
    const user = this.applicationUserService.currentUserValue;
    this.hasUserRole = !!user?.roles?.includes(Role.User);

    if (!this.hasUserRole) {
      this.loading = false;
      return;
    }

    this.authorizedBranches = user?.branchList || [];

    if (this.authorizedBranches.length <= 1) {
      this.loading = false;
      return;
    }

    this.loadAllBranchesBalance();
    this.accountTransactionService.refreshIndex.subscribe(() => {
      this.loadAllBranchesBalance();
    });
  }

  private buildCards(): BalanceKpiCard[] {
    return (this.balances || []).filter((item) => hasBalance(item)).map((item) => balanceKpiCard(item, { showSubType: true }));
  }

  get headerTitle(): string {
    return `${this.authorizedBranches.length} Şube`;
  }

  private loadAllBranchesBalance(): void {
    const branchIds = (this.authorizedBranches || [])
      .map((branch) => branch.id)
      .filter((id): id is number => id != null);

    if (!branchIds.length) {
      this.balances = [];
      this.cards = [];
      this.loading = false;
      return;
    }

    this.loading = true;

    Promise.all(
      branchIds.map((id) =>
        this.widgetService
          .getBranchBalancesWithMerge(id)
          .toPromise()
          .then((response) => (response?.isSuccess && response?.data ? response.data : []))
          .catch(() => [] as AccountBalanceDTO[])
      )
    )
      .then((lists) => {
        this.balances = this.mergeBalances(lists);
        this.cards = this.buildCards();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private mergeBalances(lists: AccountBalanceDTO[][]): AccountBalanceDTO[] {
    const merged = new Map<string, AccountBalanceDTO>();

    (lists || []).forEach((list) => {
      (list || []).forEach((balance) => {
        const key = `${balance.currency}|${balance.subType ?? ''}`;
        const existing = merged.get(key);

        if (existing) {
          existing.balance = (existing.balance || 0) + (balance.balance || 0);
          existing.customersBalance = (existing.customersBalance || 0) + (balance.customersBalance || 0);
          existing.finalBalance = (existing.finalBalance || 0) + (balance.finalBalance || 0);
        } else {
          merged.set(key, {
            currency: balance.currency,
            subType: balance.subType,
            balance: balance.balance || 0,
            customersBalance: balance.customersBalance || 0,
            finalBalance: balance.finalBalance || 0,
            endDate: balance.endDate,
          });
        }
      });
    });

    return Array.from(merged.values()).sort((a, b) => {
      const currencyCompare = (a.currency || 0) - (b.currency || 0);
      if (currencyCompare !== 0) {
        return currencyCompare;
      }

      return (a.subType || CurrencySubTypes.Has) - (b.subType || CurrencySubTypes.Has);
    });
  }
}
